import React from "react";
import { SchemaField } from "@/types/schemaBuilder";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SchemaPreviewProps {
  schema: Record<string, SchemaField>;
}

const SchemaPreview: React.FC<SchemaPreviewProps> = ({ schema }) => {
  // Convert the schema to a JSON Schema format
  const generateJsonSchema = () => {
    const jsonSchema = {
      type: "object",
      properties: {},
      required: [] as string[],
    };

    Object.entries(schema).forEach(([fieldName, field]) => {
      const schemaField: any = {
        type: field.type,
        title: field.title || fieldName,
      };

      if (field.description) {
        schemaField.description = field.description;
      }

      if (field.nodes) {
        schemaField.nodes = field.nodes;
      }

      if (field.notes) {
        schemaField.notes = field.notes;
      }

      // Add validation rules
      if (field.validation) {
        Object.entries(field.validation).forEach(([key, value]) => {
          if (value !== undefined) {
            schemaField[key] = value;
          }
        });
      }

      // Handle nested objects
      if (field.type === "object" && field.properties) {
        schemaField.properties = {};
        schemaField.required = [];

        Object.entries(field.properties).forEach(([propName, propField]) => {
          schemaField.properties[propName] = {
            type: propField.type,
            title: propField.title || propName,
          };

          if (propField.description) {
            schemaField.properties[propName].description =
              propField.description;
          }

          if (propField.required) {
            schemaField.required.push(propName);
          }

          // Add validation rules for nested object properties
          if (propField.validation) {
            Object.entries(propField.validation).forEach(([key, value]) => {
              if (value !== undefined) {
                schemaField.properties[propName][key] = value;
              }
            });
          }
        });

        if (schemaField.required.length === 0) {
          delete schemaField.required;
        }
      }

      // Handle arrays
      if (field.type === "array" && field.items) {
        schemaField.items = {
          type: field.items.type,
        };

        if (field.items.title) {
          schemaField.items.title = field.items.title;
        }

        if (field.items.description) {
          schemaField.items.description = field.items.description;
        }

        // Add validation rules for array items
        if (field.items.validation) {
          Object.entries(field.items.validation).forEach(([key, value]) => {
            if (value !== undefined) {
              schemaField.items[key] = value;
            }
          });
        }

        // Handle object items in arrays
        if (field.items.type === "object" && field.items.properties) {
          schemaField.items.properties = {};
          schemaField.items.required = [];

          Object.entries(field.items.properties).forEach(
            ([propName, propField]) => {
              schemaField.items.properties[propName] = {
                type: propField.type,
                title: propField.title || propName,
              };

              if (propField.description) {
                schemaField.items.properties[propName].description =
                  propField.description;
              }

              if (propField.required) {
                schemaField.items.required.push(propName);
              }

              // Add validation rules for object properties in array items
              if (propField.validation) {
                Object.entries(propField.validation).forEach(([key, value]) => {
                  if (value !== undefined) {
                    schemaField.items.properties[propName][key] = value;
                  }
                });
              }
            },
          );

          if (schemaField.items.required.length === 0) {
            delete schemaField.items.required;
          }
        }
      }

      jsonSchema.properties[fieldName] = schemaField;

      if (field.required) {
        jsonSchema.required.push(fieldName);
      }
    });

    if (jsonSchema.required.length === 0) {
      delete jsonSchema.required;
    }

    return jsonSchema;
  };

  const jsonSchema = generateJsonSchema();

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <CardTitle>JSON Schema Preview</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        <ScrollArea className="h-full">
          <pre className="text-sm bg-muted p-4 rounded-md overflow-auto">
            {JSON.stringify(jsonSchema, null, 2)}
          </pre>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default SchemaPreview;
